import Grid from "@mui/material/Grid";
import NTMXGrid from "../components/NTMXGrid";
import React,{useContext,useState} from "react";
import StartIconButton from "../components/buttons/StartIconButton";
import CreateIcon from '@mui/icons-material/Create';
import {DASH_AWARDS,getErrorMessage,post,put} from "../services/Client";
import {useSnackbar} from "notistack";
import {useQueryClient} from "react-query";
import {useGetCustomAwards,useGetOrganizations} from "../services/ContentManager";
import CustomAwardModal from "../components/modals/CustomAwardModal";
import {useTranslation} from "react-i18next";
import {useAwardTypeNames} from "../constants/awardType";
import EditIcon from "@mui/icons-material/Edit";
import {IconButton} from "@mui/material";
import {AbilityContext} from "../services/Can";
import {resources} from "../services/ability";

export default function CustomAwards() {

    const {t} = useTranslation();
    let [isEditing, setIsEditing] = useState(false);
    let [editAward, setEditAward] = useState({});
    // Lista paginata lato server (la dashboard non scarica più tutti i custom award).
    // Il filtro server-side gira solo su name/description (stringhe); l'esclusione degli auto-generati
    // (issuer=2) è fatta lato backend nella criteria query.
    let [inputFilters, setInputFilters] = useState({
        "filters": [],
        "pagination": {"page": 0, "pageSize": 25},
        "sort": {"field": "id", "sort": "desc"}
    });
    const inputFiltersEncripted = btoa(JSON.stringify(inputFilters));
    let {customAwards, status} = useGetCustomAwards(inputFiltersEncripted)
    const typeLabels = useAwardTypeNames()
    const ability = useContext(AbilityContext);
    let { organizations = [] } = useGetOrganizations(); //tutte le organizzazioni a cui puo' accedere l'utente loggato

    const {enqueueSnackbar} = useSnackbar();
    let queryClient = useQueryClient();

    const handleFilterModelChange = React.useCallback((filterModel) => {
        const filters = filterModel.items.map(f => ({
            "filter": f.columnField,
            "operation": f.operatorValue,
            "value": !f.value ? [] : Array.isArray(f.value) ? f.value : [f.value]
        }));
        setInputFilters(prev => ({...prev, "filters": filters}));
    }, []);
    const handlePageChange = React.useCallback((newPage) => {
        setInputFilters(prev => ({...prev, "pagination": {"page": newPage, "pageSize": prev.pagination.pageSize}}));
    }, []);
    const handlePageSizeChange = React.useCallback((newPageSize) => {
        setInputFilters(prev => ({...prev, "pagination": {"page": 0, "pageSize": newPageSize}}));
    }, []);
    const handleSortModelChange = React.useCallback((newSortModel) => {
        if (newSortModel.length > 0) setInputFilters(prev => ({...prev, "sort": newSortModel[0]}));
    }, []);

    const defaultColumns = [
        {
            headerName: t('id'),
            field: 'id',
            width: 200,
            filterable: false,
        },
        {
            headerName: t('name'),
            field: 'name',
            width: 200,
        },
        {
            headerName: t('description'),
            field: 'description',
            width: 200
        },
        {
            headerName: t('value'),
            field: 'value',
            width: 200,
            filterable: false,
        },
        {
            headerName: t('type'),
            field: 'type',
            width: 200,
            filterable: false,
            valueGetter: (params) => params.value || params.value === 0 ? typeLabels[params.value] : ""
        },
        {
            headerName: t('winningsAllowed'),
            field: 'winningsAllowed',
            width: 300,
            filterable: false,
            renderCell: (params) => params.value || "∞"
        },
        {
            headerName: t('inviteRewardColumn', 'Invita un amico'),
            field: 'inviteReward',
            width: 200,
            sortable: false,
            filterable: false,
            valueGetter: (params) => {
                const roles = [];
                if (params.row.inviterReward) roles.push(t('inviterRewardShort', 'Invitante'));
                if (params.row.invitedReward) roles.push(t('invitedRewardShort', 'Invitato'));
                return roles.join(' + ');
            }
        },
        {
            headerName: ' ',
            field: 'editDelete',
            width: 60,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            resizable: false,
            disableExport: true,
            hideable:false,
            renderCell: (params) => {
                if(params.row.endDate < new Date()) return;
                return <Grid container direction={"row"}>
                    <IconButton aria-label="edit"
                                onClick={() => {
                                    setEditAward(params.row)
                                    setIsEditing(true)
                                }}
                    >
                        <EditIcon/>
                    </IconButton>
                </Grid>
            },
        }
    ];

    const edit = (values) => {
        if(values.organization === "all") delete values.organization
        if(values.id) {
            enqueueSnackbar(t('saving...'), {variant: "info"});
            put(`${DASH_AWARDS}/customs/${values.id}`, {body: values})
                .then(() => enqueueSnackbar(t('saved'), {variant: "success"}))
                .catch(e => enqueueSnackbar(getErrorMessage(e), {variant: "error"}))
                .finally(() => queryClient.invalidateQueries("AwardsCustom"));
        } else {
            enqueueSnackbar(t('saving...'), {variant: "info"});
            post(`${DASH_AWARDS}/customs`, {body: values})
                .then(() => enqueueSnackbar(t('saved'), {variant: "success"}))
                .catch(e => enqueueSnackbar(getErrorMessage(e), {variant: "error"}))
                .finally(() => queryClient.invalidateQueries("AwardsCustom"));
        }
    }

    return <Grid>
        <NTMXGrid
            key={"custom-awards-table"}
            columns={defaultColumns}
            rows={customAwards?.content || []}
            title={t('customAwards')}
            loading={status !== "success"}
            onFilterModelChange={handleFilterModelChange}
            filterMode="server"
            onSortModelChange={handleSortModelChange}
            sortingMode="server"
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            paginationMode="server"
            rowCount={customAwards?.totalElements || customAwards?.page?.totalElements || 0}
            rightButton={<Grid container justifyContent={"flex-end"}>
                <StartIconButton onClick={() => setIsEditing(true)} title={t('createAward')}
                                 startIcon={<CreateIcon/>}/>
            </Grid>}
            initialState={{ pinnedColumns: { right: ['editDelete'] } }}
        />
        <CustomAwardModal open={!!isEditing}
                          onClose={() => {
                              setIsEditing(false)
                              setEditAward({})
                          }}
                          onSubmit={edit} defaultValues={editAward}
                          warningMessage={Object.keys(editAward).length !== 0 && t('editCustomAwardWarningMessage')}
                          organizations={
                                ability.can("write", resources.ALL_ORG_CUSTOM_AWARD) ?
                                    [{id: "all", title: t('all')}, ...organizations] :
                                    organizations
                          }
        />
    </Grid>

}
